import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO,
} from 'date-fns';
import { fr } from 'date-fns/locale';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(config => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function fmtDateFR(dateStr) {
  if (!dateStr) return '—';
  try { return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR'); } catch { return dateStr; }
}

function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '28px 32px', width: wide ? 620 : 520, maxWidth: '95vw', maxHeight: '92vh', overflowY: 'auto', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1, padding: '0 4px' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, type = 'text', placeholder, disabled, hint }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</label>
      <input
        type={type} value={value || ''} onChange={e => onChange && onChange(e.target.value)}
        placeholder={placeholder} disabled={disabled}
        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid var(--border)', background: disabled ? 'var(--bg3)' : 'var(--bg2)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }}
      />
      {hint && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

const EMPTY_FORM = {
  date: '', heure: '', libelle: '',
  ref: '',
  ref_commande: '', ref_work_order: '',
  client_final: '', technicien: '', adresse: '',
  rappels_personnalises: [],
};

export default function Agenda() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [nacelleEvents, setNacelleEvents] = useState([]);
  const [modal, setModal] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [lookup, setLookup] = useState({ loading: false, found: null, error: null });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newRappelJours, setNewRappelJours] = useState('');
  const lookupTimer = useRef(null);

  const loadEvents = useCallback(() => {
    api.get('/agenda').then(r => setEvents(r.data.events || [])).catch(() => {});
  }, []);
  const loadNacelle = useCallback(() => {
    api.get('/admin/dashboard/alertes').then(r => setNacelleEvents(r.data.nacelle || [])).catch(() => {});
  }, []);

  useEffect(() => { loadEvents(); loadNacelle(); }, [loadEvents, loadNacelle]);

  // Calendar grid
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calDays = eachDayOfInterval({ start: calStart, end: calEnd });

  const getEventsForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const custom = events.filter(e => e.date === dayStr);
    const nacelle = nacelleEvents.filter(e => {
      if (!e.date) return false;
      try { return isSameDay(parseISO(e.date), day); } catch { return false; }
    });
    return { custom, nacelle };
  };

  // Zoho auto-fill (debounced 700ms)
  const handleRefChange = (val) => {
    setForm(f => ({ ...f, ref: val }));
    setLookup({ loading: false, found: null, error: null });
    clearTimeout(lookupTimer.current);
    if (val.length >= 4) {
      lookupTimer.current = setTimeout(() => doLookup(val), 700);
    }
  };

  const doLookup = async (ref) => {
    setLookup({ loading: true, found: null, error: null });
    try {
      const r = await api.get(`/agenda/zoho-lookup?ref=${encodeURIComponent(ref)}`);
      if (r.data.found) {
        setForm(f => ({
          ...f,
          ref_commande:   r.data.ref_commande   || '',
          ref_work_order: r.data.ref_work_order  || '',
          client_final:   r.data.client_final    || '',
          technicien:     r.data.technicien      || '',
          adresse:        r.data.adresse         || '',
          heure:          r.data.heure           || f.heure,
          date:           r.data.date            || f.date,
        }));
        setLookup({ loading: false, found: true, error: null });
      } else {
        setLookup({ loading: false, found: false, error: 'Référence non trouvée dans Zoho' });
      }
    } catch {
      setLookup({ loading: false, found: null, error: 'Erreur de connexion Zoho' });
    }
  };

  // Rappels personnalisés
  const addRappel = () => {
    const j = parseInt(newRappelJours, 10);
    if (!j || j < 1 || j > 365) return;
    if (form.rappels_personnalises.some(r => r.jours === j)) return;
    setForm(f => ({ ...f, rappels_personnalises: [...f.rappels_personnalises, { jours: j, sent: false }].sort((a, b) => b.jours - a.jours) }));
    setNewRappelJours('');
  };
  const removeRappel = (jours) => {
    setForm(f => ({ ...f, rappels_personnalises: f.rappels_personnalises.filter(r => r.jours !== jours) }));
  };

  const handleCreate = async () => {
    if (!form.date || !form.libelle) return;
    setSubmitting(true);
    try {
      await api.post('/agenda', {
        date: form.date, heure: form.heure, libelle: form.libelle,
        ref_commande: form.ref_commande, ref_work_order: form.ref_work_order,
        client_final: form.client_final, technicien: form.technicien, adresse: form.adresse,
        rappels_personnalises: form.rappels_personnalises,
      });
      loadEvents();
      setModal(null);
      setForm(EMPTY_FORM);
    } catch {} finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await api.delete(`/agenda/${id}`);
      loadEvents(); setModal(null); setSelectedEvent(null);
    } catch {} finally { setDeleting(false); }
  };

  const openCreate = (day) => {
    setForm({ ...EMPTY_FORM, date: format(day, 'yyyy-MM-dd') });
    setLookup({ loading: false, found: null, error: null });
    setNewRappelJours('');
    setModal('create');
  };
  const openView = (ev, type) => {
    setSelectedEvent({ ...ev, _type: type });
    setModal('view');
  };

  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');

  return (
    <div style={{ padding: '24px 28px', height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', margin: 0 }}>Agenda</h1>
          <p style={{ color: 'var(--text3)', fontSize: 12, marginTop: 3, marginBottom: 0 }}>Événements planifiés · Rappels WhatsApp automatiques · Nacelle Zoho</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => { loadEvents(); loadNacelle(); }} style={{ padding: '7px 13px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text3)', fontSize: 12, cursor: 'pointer' }}>
            ↻ Actualiser
          </button>
          <button onClick={() => openCreate(today)} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            + Nouvel événement
          </button>
        </div>
      </div>

      {/* Month nav + legend */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} style={navBtn}>‹</button>
          <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', minWidth: 180, textAlign: 'center', textTransform: 'capitalize' }}>
            {format(currentMonth, 'MMMM yyyy', { locale: fr })}
          </span>
          <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} style={navBtn}>›</button>
          <button onClick={() => setCurrentMonth(new Date())} style={{ ...navBtn, width: 'auto', fontSize: 12, padding: '5px 14px' }}>Aujourd'hui</button>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <Legend color="var(--accent)" label="Événement personnalisé" />
          <Legend color="#F97316" label="Nacelle nécessaire (Zoho)" />
        </div>
      </div>

      {/* Calendar — flex:1 pour prendre toute la hauteur disponible */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1.5px solid var(--border)', borderRadius: 12, overflow: 'hidden', minHeight: 0 }}>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'var(--bg3)', borderBottom: '1.5px solid var(--border)', flexShrink: 0 }}>
          {JOURS.map(j => (
            <div key={j} style={{ textAlign: 'center', padding: '10px 4px', fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.07em' }}>{j}</div>
          ))}
        </div>

        {/* Day cells grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: `repeat(${calDays.length / 7}, 1fr)`, flex: 1, minHeight: 0 }}>
          {calDays.map((day, i) => {
            const { custom, nacelle } = getEventsForDay(day);
            const isToday = isSameDay(day, today);
            const inMonth = isSameMonth(day, currentMonth);
            const allEvs = [...custom.map(e => ({ ...e, _type: 'custom' })), ...nacelle.map(e => ({ ...e, _type: 'nacelle' }))];
            const visible = allEvs.slice(0, 3);
            const hidden = allEvs.length - 3;

            return (
              <div
                key={i}
                onClick={() => openCreate(day)}
                style={{
                  padding: '5px 6px 4px', cursor: 'pointer',
                  borderTop: i >= 7 ? '1px solid var(--border)' : 'none',
                  borderLeft: i % 7 !== 0 ? '1px solid var(--border)' : 'none',
                  background: isToday ? 'color-mix(in srgb, var(--accent) 7%, var(--bg))' : 'var(--bg)',
                  opacity: inMonth ? 1 : 0.3,
                  overflow: 'hidden',
                  transition: 'background .12s',
                }}
                onMouseEnter={e => { if (!isToday) e.currentTarget.style.background = 'var(--bg2)'; }}
                onMouseLeave={e => { if (!isToday) e.currentTarget.style.background = 'var(--bg)'; }}
              >
                <div style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 24, height: 24, borderRadius: '50%', fontSize: 12,
                  fontWeight: isToday ? 700 : 400,
                  background: isToday ? 'var(--accent)' : 'transparent',
                  color: isToday ? '#fff' : 'var(--text)',
                  marginBottom: 2,
                }}>
                  {format(day, 'd')}
                </div>
                {visible.map((ev, idx) => (
                  <div
                    key={ev.id || idx}
                    onClick={e => { e.stopPropagation(); openView(ev, ev._type); }}
                    title={ev._type === 'nacelle' ? `Nacelle — ${ev.client || ev.ref_commande}` : (ev.libelle || ev.ref_commande)}
                    style={{
                      background: ev._type === 'nacelle' ? '#F97316' : 'var(--accent)',
                      color: '#fff', borderRadius: 4, fontSize: 11, padding: '2px 6px', marginTop: 2,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      cursor: 'pointer', lineHeight: '15px',
                    }}
                  >
                    {ev._type === 'nacelle' ? `🪜 ${ev.client || ev.ref_commande || 'Nacelle'}` : (ev.libelle || ev.ref_commande || 'Événement')}
                  </div>
                ))}
                {hidden > 0 && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>+{hidden} autre{hidden > 1 ? 's' : ''}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Prochains événements */}
      <UpcomingList events={events} todayStr={todayStr} onView={(ev) => openView(ev, 'custom')} />

      {/* ── Modal création ── */}
      {modal === 'create' && (
        <Modal title="Nouvel événement" onClose={() => { setModal(null); setForm(EMPTY_FORM); }} wide>
          {/* Ref Zoho auto-fill */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.06em' }}>
              Réf. Commande ou Réf. Work Order — auto-remplissage Zoho
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text" value={form.ref}
                onChange={e => handleRefChange(e.target.value)}
                placeholder="Ex: O260114_99500 ou W260422_44701"
                style={{
                  width: '100%', padding: '9px 38px 9px 12px', borderRadius: 8,
                  border: `1.5px solid ${lookup.found === true ? '#3B6D11' : lookup.error ? '#D42B2B' : 'var(--border)'}`,
                  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box', outline: 'none',
                }}
              />
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13 }}>
                {lookup.loading ? '⏳' : lookup.found === true ? '✅' : lookup.error ? '❌' : '🔍'}
              </span>
            </div>
            {lookup.found === true && <div style={{ fontSize: 11, color: '#3B6D11', marginTop: 3 }}>✅ Dossier trouvé — informations remplies automatiquement</div>}
            {lookup.error && <div style={{ fontSize: 11, color: '#D42B2B', marginTop: 3 }}>⚠️ {lookup.error}</div>}
          </div>

          {/* Date + Heure */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Inp label="Date *" type="date" value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} />
            <Inp label="Heure" type="time" value={form.heure} onChange={v => setForm(f => ({ ...f, heure: v }))} />
          </div>

          <Inp label="Libellé / Motif *" value={form.libelle} onChange={v => setForm(f => ({ ...f, libelle: v }))} placeholder="Ex: Pompe de relevage à louer, Nacelle à réserver…" />

          {/* Zoho info block */}
          {lookup.found === true ? (
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Informations Zoho</div>
              {[
                ['Réf. commande', form.ref_commande],
                ['Work Order', form.ref_work_order],
                ['Client final', form.client_final],
                ['Technicien', form.technicien],
                ['Adresse', form.adresse],
              ].filter(item => item[1]).map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span style={{ color: 'var(--text3)' }}>{label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text)', textAlign: 'right', maxWidth: '65%' }}>{val}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Inp label="Réf. commande" value={form.ref_commande} onChange={v => setForm(f => ({ ...f, ref_commande: v }))} placeholder="O26..." />
              <Inp label="Work Order" value={form.ref_work_order} onChange={v => setForm(f => ({ ...f, ref_work_order: v }))} placeholder="W26..." />
              <Inp label="Client final" value={form.client_final} onChange={v => setForm(f => ({ ...f, client_final: v }))} style={{ gridColumn: '1 / -1' }} />
              <Inp label="Technicien" value={form.technicien} onChange={v => setForm(f => ({ ...f, technicien: v }))} />
              <Inp label="Adresse" value={form.adresse} onChange={v => setForm(f => ({ ...f, adresse: v }))} />
            </div>
          )}

          {/* Rappels */}
          <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px', marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
              📲 Rappels WhatsApp
            </div>

            {/* Rappels automatiques */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {[{ label: 'J-2 (automatique)', jours: 2 }, { label: 'J-1 (automatique)', jours: 1 }].map(r => (
                <span key={r.jours} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, background: 'color-mix(in srgb, var(--accent) 12%, var(--bg))', color: 'var(--accent)', border: '1px solid color-mix(in srgb, var(--accent) 30%, var(--border))', fontWeight: 600 }}>
                  🔔 {r.label}
                </span>
              ))}
              {form.rappels_personnalises.map(r => (
                <span key={r.jours} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 12px', borderRadius: 20, fontSize: 12, background: '#FFF8E1', color: '#7d5a00', border: '1px solid #F9CA24', fontWeight: 600 }}>
                  🔔 J-{r.jours} (perso)
                  <button onClick={() => removeRappel(r.jours)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7d5a00', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
                </span>
              ))}
            </div>

            {/* Ajouter rappel personnalisé */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>+ Rappel J-</span>
              <input
                type="number" min="1" max="365" value={newRappelJours}
                onChange={e => setNewRappelJours(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addRappel()}
                placeholder="ex: 7"
                style={{ width: 70, padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none' }}
              />
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>jours avant</span>
              <button
                onClick={addRappel}
                disabled={!newRappelJours}
                style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: newRappelJours ? 'var(--accent)' : 'var(--bg3)', color: newRappelJours ? '#fff' : 'var(--text3)', fontSize: 12, fontWeight: 700, cursor: newRappelJours ? 'pointer' : 'not-allowed' }}
              >
                Ajouter
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => { setModal(null); setForm(EMPTY_FORM); }} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, cursor: 'pointer' }}>
              Annuler
            </button>
            <button
              onClick={handleCreate}
              disabled={submitting || !form.date || !form.libelle}
              style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: (!form.date || !form.libelle) ? 'var(--bg3)' : 'var(--accent)', color: (!form.date || !form.libelle) ? 'var(--text3)' : '#fff', fontSize: 13, fontWeight: 700, cursor: (!form.date || !form.libelle) ? 'not-allowed' : 'pointer' }}
            >
              {submitting ? 'Enregistrement…' : '✅ Enregistrer'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Modal vue événement ── */}
      {modal === 'view' && selectedEvent && (
        <Modal
          title={selectedEvent._type === 'nacelle' ? '🪜 Nacelle nécessaire' : '📅 Détail événement'}
          onClose={() => { setModal(null); setSelectedEvent(null); }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {[
              ['Date', fmtDateFR(selectedEvent.date)],
              ['Heure', selectedEvent.heure || selectedEvent.heure_intervention || '—'],
              ['Libellé', selectedEvent.libelle || '—'],
              ['Réf. commande', selectedEvent.ref_commande || '—'],
              ['Work Order', selectedEvent.ref_work_order || '—'],
              ['Client final', selectedEvent.client_final || selectedEvent.client || '—'],
              ['Technicien', selectedEvent.technicien || '—'],
              ['Adresse', selectedEvent.adresse || '—'],
              ...(selectedEvent._type === 'nacelle' ? [['Type', selectedEvent.typo || '—'], ['Statut', selectedEvent.stage || '—']] : []),
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text3)', fontWeight: 600, minWidth: 140 }}>{label}</span>
                <span style={{ color: 'var(--text)', textAlign: 'right' }}>{val}</span>
              </div>
            ))}
          </div>

          {selectedEvent._type === 'custom' && Array.isArray(selectedEvent.rappels_personnalises) && selectedEvent.rappels_personnalises.length > 0 && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--bg3)', borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Rappels WhatsApp</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[{ label: 'J-2', sent: selectedEvent.rappel_j2_sent }, { label: 'J-1', sent: selectedEvent.rappel_j1_sent }].map(r => (
                  <span key={r.label} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, background: r.sent ? '#EAF3DE' : 'var(--bg2)', color: r.sent ? '#27500A' : 'var(--text3)', border: `1px solid ${r.sent ? '#3B6D11' : 'var(--border)'}` }}>
                    {r.sent ? '✅' : '⏳'} {r.label}
                  </span>
                ))}
                {selectedEvent.rappels_personnalises.map(r => (
                  <span key={r.jours} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, background: r.sent ? '#EAF3DE' : '#FFF8E1', color: r.sent ? '#27500A' : '#7d5a00', border: `1px solid ${r.sent ? '#3B6D11' : '#F9CA24'}` }}>
                    {r.sent ? '✅' : '⏳'} J-{r.jours}
                  </span>
                ))}
              </div>
            </div>
          )}

          {selectedEvent._type === 'nacelle' ? (
            <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--bg3)', borderRadius: 8, fontSize: 12, color: 'var(--text3)' }}>
              Cet événement vient de Zoho CRM (Nacelle à prévoir). Modifiez-le directement dans Zoho.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                onClick={() => handleDelete(selectedEvent.id)}
                disabled={deleting}
                style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #D42B2B', background: 'transparent', color: '#D42B2B', fontSize: 13, fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer' }}
              >
                {deleting ? 'Suppression…' : '🗑 Supprimer'}
              </button>
              <button onClick={() => { setModal(null); setSelectedEvent(null); }} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, cursor: 'pointer' }}>
                Fermer
              </button>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

const navBtn = {
  width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', cursor: 'pointer',
  fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
};

function Legend({ color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)' }}>
      <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: color, flexShrink: 0 }} />
      {label}
    </div>
  );
}

function UpcomingList({ events, todayStr, onView }) {
  const upcoming = events
    .filter(e => e.date >= todayStr)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 8);
  if (upcoming.length === 0) return null;

  return (
    <div style={{ marginTop: 20 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 10, marginTop: 0 }}>Prochains événements</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
        {upcoming.map(ev => {
          const allSent = ev.rappel_j2_sent && ev.rappel_j1_sent &&
            (ev.rappels_personnalises || []).every(r => r.sent);
          return (
            <div
              key={ev.id}
              onClick={() => onView(ev)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg2)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', transition: 'border-color .15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <div style={{ textAlign: 'center', minWidth: 40 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--accent)', lineHeight: 1 }}>
                  {new Date(ev.date + 'T00:00:00').getDate()}
                </div>
                <div style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--text3)', letterSpacing: '.04em' }}>
                  {new Date(ev.date + 'T00:00:00').toLocaleDateString('fr-FR', { month: 'short' })}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.libelle || ev.ref_commande || 'Événement'}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[ev.heure, ev.client_final, ev.technicien].filter(Boolean).join(' · ')}
                </div>
              </div>
              <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0, background: allSent ? '#EAF3DE' : 'var(--bg3)', color: allSent ? '#27500A' : 'var(--text3)', border: `1px solid ${allSent ? '#3B6D11' : 'var(--border)'}` }}>
                {allSent ? '✅ rappels OK' : '⏳ rappels à venir'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
